import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Button,
  Space,
  Typography,
  Tabs,
  Tag,
  Skeleton,
  Alert,
  Empty,
  Tooltip,
  message,
} from 'antd';
import {
  ExperimentOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  SafetyOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
  CodeOutlined,
  RobotOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { enqueueValidationRun, newRequestKey } from '@/api/validationRuns';
import { HtmlReportDrawer } from './HtmlReportDrawer';
import { RunTargetDrawer, type RunTarget } from './RunTargetDrawer';
import { useStore } from '@/stores';
import type {
  ScenarioTurnSpec,
  ScenarioTurnExpectation,
  ScenarioConversationAssertion,
  ScenarioRunHistoryItem,
  ScenarioSummary,
} from '@/api/scenarios';
import { colors } from '@/theme/tokens';
import { StatusPill } from '@/components/StatusPill';

const { Text, Paragraph } = Typography;

// Polished read-only viewer for one scenario file. Three tabs:
//   • Conversation — turn-by-turn user messages with their per-turn expectations
//   • Conversation rules — invariants applied across the whole conversation
//   • Run history — the last 30 runs of this scenario from aivalidation.*

export const ScenarioDetailDrawer = observer(function ScenarioDetailDrawer() {
  const { scenariosStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  // Drawer stays open whenever there's anything to show: the loading
  // skeleton, the detail body, OR an error message. Without `detailError`
  // here the drawer would silently close on a failed fetch — leaving the
  // user wondering whether they clicked the wrong thing.
  const open = store.openDetail != null || store.isLoadingDetail || store.detailError != null;
  const detail = store.openDetail;
  const summary = detail?.summary;
  const parsed = detail?.parsed;

  // Controlled tab state. We lift it here so the Run button can flip the
  // user to the Run-history tab the instant they click — clinical SMEs
  // shouldn't have to discover where the result lands.
  const [activeTab, setActiveTab] = useState<string>('turns');

  // Auto-switch to history whenever a run becomes pending. This is the
  // central UX move for nurses: clicking Run feels like a single action
  // that "shows me the result," not "submits a request and disappears."
  useEffect(() => {
    if (store.pendingRunStartedAt != null) {
      setActiveTab('history');
    }
  }, [store.pendingRunStartedAt]);

  // Reset tab to Conversation when a different scenario is opened.
  useEffect(() => {
    if (summary?.scenarioId) setActiveTab('turns');
  }, [summary?.scenarioId]);

  return (
    <Drawer
      open={open}
      onClose={() => store.closeDetail()}
      width={720}
      title={
        summary ? (
          <DrawerTitle summary={summary} />
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <ExperimentOutlined /> Scenario
          </span>
        )
      }
      destroyOnClose
      styles={{ body: { padding: 0, background: isDark ? colors.slate[900] : colors.slate[50] } }}
    >
      {store.isLoadingDetail ? (
        <div style={{ padding: '20px 24px' }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      ) : store.detailError ? (
        <div style={{ padding: '20px 24px' }}>
          <Alert type="error" showIcon message={store.detailError} />
        </div>
      ) : detail && summary && parsed ? (
        <div style={{ padding: '4px 0 0' }}>
          {/* Hero block with description + meta */}
          <HeroBlock summary={summary} parsed={parsed} isDark={isDark} rawJson={detail.rawJson} />

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            tabBarStyle={{ padding: '0 22px', marginBottom: 0 }}
            items={[
              {
                key: 'turns',
                label: (
                  <span>
                    <ThunderboltOutlined /> Conversation
                  </span>
                ),
                children: (
                  <div style={{ padding: '14px 22px 24px' }}>
                    <TurnsView turns={parsed.turns ?? []} isDark={isDark} />
                  </div>
                ),
              },
              {
                key: 'rules',
                label: (
                  <span>
                    <SafetyOutlined /> Conversation rules
                  </span>
                ),
                children: (
                  <div style={{ padding: '14px 22px 24px' }}>
                    <ConversationRules
                      rules={parsed.conversationLevelAssertions ?? []}
                      isDark={isDark}
                    />
                  </div>
                ),
              },
              {
                key: 'history',
                label: (
                  <span>
                    <HistoryOutlined /> Run history
                    {store.runHistory.length > 0 && (
                      <span
                        style={{
                          fontSize: 10.5,
                          fontWeight: 600,
                          marginLeft: 6,
                          padding: '0 6px',
                          borderRadius: 999,
                          background: isDark ? colors.slate[700] : colors.slate[200],
                          color: isDark ? colors.slate[300] : colors.slate[600],
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      >
                        {store.runHistory.length}
                      </span>
                    )}
                  </span>
                ),
                children: (
                  <div style={{ padding: '14px 22px 24px' }}>
                    <RunHistoryView
                      isLoading={store.isLoadingHistory}
                      rows={store.runHistory}
                      isDark={isDark}
                      pendingStartedAt={store.pendingRunStartedAt}
                    />
                  </div>
                ),
              },
              {
                key: 'json',
                label: (
                  <span>
                    <CodeOutlined /> Raw JSON
                  </span>
                ),
                children: (
                  <div style={{ padding: '14px 22px 24px' }}>
                    <RawJsonView json={detail.rawJson} isDark={isDark} />
                  </div>
                ),
              },
            ]}
          />
        </div>
      ) : null}
    </Drawer>
  );
});

// ── Drawer title (compact metadata strip) ──────────────────────────────────

function DrawerTitle({ summary }: { summary: ScenarioSummary }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <ExperimentOutlined />
      <span
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 14,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
          flex: 1,
        }}
      >
        {summary.scenarioId}
      </span>
      {/* Layman-language explanation of what this drawer is showing — what the
          conversation, the rules, and the run history mean for a non-engineer
          reader. Engineers can ignore; SMEs reach for it first. */}
      <HelpHotspot topic="validations.scenarios.detail" />
    </div>
  );
}

// ── Hero block with category, agent, key flags, copy/open actions ──────────

function HeroBlock({
  summary,
  parsed,
  isDark,
  rawJson,
}: {
  summary: ScenarioSummary;
  parsed: { agentId: number; merchantId: number; memberId: number };
  isDark: boolean;
  rawJson: string;
}) {
  const fg =
    summary.category === 'A' || summary.category === 'G'
      ? colors.red[600]
      : summary.category === 'B' || summary.category === 'I'
      ? colors.amber[600]
      : summary.category === 'E'
      ? colors.green[600]
      : summary.category === 'D' || summary.category === 'DR' || summary.category === 'K'
      ? colors.violet[600]
      : colors.ink[600];

  return (
    <div
      style={{
        padding: '18px 22px',
        background: isDark
          ? `linear-gradient(135deg, ${fg}22 0%, ${colors.slate[900]} 60%)`
          : `linear-gradient(135deg, ${fg}10 0%, #FFFFFF 60%)`,
        borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            fontWeight: 700,
            background: fg,
            color: '#FFFFFF',
            padding: '3px 9px',
            borderRadius: 6,
            letterSpacing: '0.04em',
          }}
        >
          {summary.category} · {summary.categoryHint}
        </span>
        <Tag
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            background: isDark ? colors.slate[800] : '#FFFFFF',
            border: `1px solid ${isDark ? colors.slate[600] : colors.slate[200]}`,
            color: isDark ? colors.slate[200] : colors.slate[700],
            margin: 0,
          }}
        >
          <RobotOutlined style={{ fontSize: 11, marginRight: 4 }} /> {summary.agentCanonicalName}
        </Tag>
        {summary.isMultiTurn && <StatusPill tone="violet">Multi-turn · {summary.turnCount}</StatusPill>}
        {summary.isSafety && (
          <StatusPill tone="red" icon={<SafetyOutlined />}>
            Safety
          </StatusPill>
        )}
        {summary.isAdversarial && <StatusPill tone="amber">Red-team</StatusPill>}
      </div>
      {summary.description && (
        <Paragraph
          style={{
            fontSize: 13.5,
            lineHeight: 1.55,
            margin: 0,
            color: isDark ? colors.slate[200] : colors.slate[700],
          }}
        >
          {summary.description}
        </Paragraph>
      )}
      <div
        style={{
          display: 'flex',
          gap: 14,
          marginTop: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <MetaPair label="Agent id" value={`#${parsed.agentId}`} isDark={isDark} />
        <MetaPair label="Merchant id" value={`#${parsed.merchantId}`} isDark={isDark} />
        <MetaPair label="Member id" value={`#${parsed.memberId}`} isDark={isDark} />
        <span style={{ flex: 1 }} />
        <RunScenarioButton summary={summary} />
        <Tooltip title="Copy raw JSON">
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => {
              void navigator.clipboard?.writeText(rawJson);
              message.success('JSON copied');
            }}
          >
            Copy
          </Button>
        </Tooltip>
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[500] : colors.slate[500],
          wordBreak: 'break-all',
        }}
      >
        {summary.filePath}
      </div>
    </div>
  );
}

// ── Per-scenario "Run" button ─────────────────────────────────────────────
//
// Designed for a clinical SME, not an engineer. Three rules:
//
//  1. The button itself is the status surface. It shows the user's last
//     action and its outcome — "Run", "Running…", "Last run passed",
//     "Last run failed". No need to switch screens or hunt for results.
//
//  2. Cause-and-effect is visible. Clicking Run instantly switches the
//     drawer to the Run-history tab and shows a "Running now…" placeholder
//     row. The nurse sees exactly where the result will land.
//
//  3. We poll the API in the background until the new run row appears.
//     The page never asks the user to refresh.
//
// One scenario × Foundry round-trips ≈ a few cents of credits and 30s–3min
// wall time. We don't hard-confirm (cost is tiny); we DO communicate that
// a run was kicked off via the button label change.

const RunScenarioButton = observer(function RunScenarioButton({
  summary,
}: {
  summary: ScenarioSummary;
}) {
  const { scenariosStore: store } = useStore();
  const [submitting, setSubmitting] = useState(false);
  // Review R10: one key per intended run, kept across a failed or uncertain submit and dropped only once the server
  // has answered with a job. A retry then returns the same job instead of starting a second paid run.
  const pendingKey = useRef<string | null>(null);

  // Three states drive the button's appearance:
  //   pending  — a run we started is in flight (we have pendingRunStartedAt)
  //   lastRun  — a previous run exists; show its outcome as the button label
  //   idle     — never run before
  const isPending = store.pendingRunStartedAt != null;
  const lastRun = !isPending && store.runHistory.length > 0 ? store.runHistory[0] : null;

  // `key` is one per intended run (per opened confirm, or kept by `pendingKey`): resending it returns the same job.
  // Returns false when the submit failed, so a confirm stays open and its retry reuses the key.
  const start = async (target: RunTarget = {}, key?: string): Promise<boolean> => {
    if (submitting || isPending) return false;
    if (!key) key = pendingKey.current ??= newRequestKey();
    setSubmitting(true);
    try {
      const resp = await enqueueValidationRun({
        agentId: summary.agentId,
        scenarioId: summary.scenarioId,
        triggerSource: 'manual-single',
        merchantId: target.merchantId,
        memberId: target.memberId,
        idempotencyKey: key,
      });
      // Mark the run pending and start the auto-poll. The drawer's
      // useEffect will switch the active tab to Run history; the
      // RunHistoryView will render a "Running now…" placeholder.
      pendingKey.current = null;
      store.markRunPending(resp.correlationId, resp.jobId);
      store.startPollingForNewRun(summary.scenarioId, summary.agentId);
      void message.success(
        'Test started. We will show the result as soon as it lands.',
        4,
      );
    } catch (err) {
      void message.error(
        target.merchantId
          ? `We could not start the test: ${(err as Error).message}`
          : 'We could not start the test. Try again, or contact support if it keeps happening.',
      );
      // Surface the underlying message in the console for engineers.
      // eslint-disable-next-line no-console
      console.error('[RunScenarioButton] enqueue failed:', err);
      return false;
    } finally {
      setSubmitting(false);
    }
    return true;
  };

  const handleClick = () => void start();

  // Run at a chosen practice and patient (EVAL-06 D-E2): practice, patient and agent in a drawer. A failed submit
  // keeps the drawer, and its request key, for the retry (review R10).
  const [targetOpen, setTargetOpen] = useState(false);
  const chooseTarget = () => setTargetOpen(true);

  // ── Compose label + tooltip per state ──────────────────────────────────
  let label: React.ReactNode;
  let tooltip: string;
  let buttonType: 'primary' | 'default' = 'primary';
  let icon: React.ReactNode = <PlayCircleOutlined />;

  if (isPending) {
    label = 'Running…';
    tooltip = 'Test is running in the background. This page will update automatically.';
    icon = null; // antd's `loading` already shows a spinner
  } else if (lastRun) {
    const ago = humanRelative(new Date(lastRun.runDate));
    if (lastRun.passed) {
      label = `Run again`;
      tooltip = `Last run passed ${ago}. Click to run this test again.`;
      icon = <PlayCircleOutlined />;
      buttonType = 'default';
    } else {
      label = `Run again`;
      tooltip = `Last run failed ${ago}. Click to run this test again.`;
      icon = <PlayCircleOutlined />;
      buttonType = 'default';
    }
  } else {
    label = 'Run';
    tooltip = 'Replay this test against the agent. Small cost — runs in the background.';
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {/* Last-run status badge — sits next to the button, not on it, so the
          button label stays a clear call to action. */}
      {!isPending && lastRun && (
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            padding: '2px 8px',
            borderRadius: 999,
            background: lastRun.passed
              ? 'rgba(5, 150, 105, 0.12)'
              : 'rgba(220, 38, 38, 0.12)',
            color: lastRun.passed ? '#059669' : '#DC2626',
            whiteSpace: 'nowrap',
          }}
        >
          {lastRun.passed ? '✓' : '✕'} Last run {humanRelative(new Date(lastRun.runDate))}
        </span>
      )}
      <Tooltip title={tooltip}>
        <Button
          size="small"
          type={buttonType}
          icon={icon}
          loading={submitting || isPending}
          onClick={handleClick}
          disabled={isPending}
        >
          {label}
        </Button>
      </Tooltip>
      {!isPending && (
        <Button size="small" type="link" onClick={chooseTarget} disabled={submitting}>
          Choose patient…
        </Button>
      )}
      {!isPending && store.lastRunProblem && (
        <Text type="warning" style={{ fontSize: 12 }}>
          {store.lastRunProblem}
        </Text>
      )}
      <RunTargetDrawer
        open={targetOpen}
        onClose={() => setTargetOpen(false)}
        agentId={summary.agentId}
        scenarioId={summary.scenarioId}
        onRun={({ target, key }) => start(target, key)}
      />
    </span>
  );
});

/**
 * Plain-language relative time, e.g. "just now", "2 min ago", "3 hours ago",
 * "yesterday", "Apr 25". Optimised for non-technical readers — no "ms" or
 * "ISO timestamp" jargon ever appears.
 */
function humanRelative(when: Date): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - when.getTime()) / 1000));
  if (diffSec < 30) return 'just now';
  if (diffSec < 90) return '1 minute ago';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} minutes ago`;
  if (diffSec < 7200) return '1 hour ago';
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hours ago`;
  if (diffSec < 172800) return 'yesterday';
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)} days ago`;
  // Older than a week — show a calendar date in the user's locale.
  return when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function MetaPair({
  label,
  value,
  isDark,
}: {
  label: string;
  value: string;
  isDark: boolean;
}) {
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: isDark ? colors.slate[500] : colors.slate[400],
          lineHeight: 1.2,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[200] : colors.slate[700],
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ── Turns view ─────────────────────────────────────────────────────────────

function TurnsView({ turns, isDark }: { turns: ScenarioTurnSpec[]; isDark: boolean }) {
  if (turns.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No turns defined." />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {turns.map((t, i) => (
        <TurnCard key={i} index={i} turn={t} isDark={isDark} />
      ))}
    </div>
  );
}

function TurnCard({
  index,
  turn,
  isDark,
}: {
  index: number;
  turn: ScenarioTurnSpec;
  isDark: boolean;
}) {
  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {/* User chat bubble row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '14px 16px',
          background: isDark ? `${colors.ink[700]}22` : colors.ink[50],
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 999,
            background: colors.ink[600],
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            flexShrink: 0,
            fontWeight: 700,
          }}
        >
          {index + 1}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: colors.ink[600],
              marginBottom: 3,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <UserOutlined /> User · turn {index + 1}
          </div>
          <div
            style={{
              fontSize: 13.5,
              lineHeight: 1.55,
              color: isDark ? colors.slate[100] : colors.slate[800],
              whiteSpace: 'pre-wrap',
            }}
          >
            {turn.user || <span style={{ fontStyle: 'italic', opacity: 0.6 }}>(empty)</span>}
          </div>
        </div>
      </div>
      {/* Expectations */}
      {turn.expect ? (
        <ExpectationBlock expect={turn.expect} isDark={isDark} />
      ) : (
        <div
          style={{
            padding: '10px 16px',
            fontSize: 11.5,
            color: isDark ? colors.slate[500] : colors.slate[400],
            fontStyle: 'italic',
          }}
        >
          No expectations on this turn — only that the agent doesn't error.
        </div>
      )}
    </div>
  );
}

function ExpectationBlock({
  expect,
  isDark,
}: {
  expect: ScenarioTurnExpectation;
  isDark: boolean;
}) {
  const sections: { title: string; tone: 'green' | 'amber' | 'red' | 'violet'; items: string[] }[] = [];
  if (expect.skillsLoaded?.length)
    sections.push({ title: 'Skills the agent must load', tone: 'violet', items: expect.skillsLoaded });
  if (expect.skillsNotLoaded?.length)
    sections.push({ title: 'Skills the agent must NOT load', tone: 'red', items: expect.skillsNotLoaded });
  if (expect.responseMustContain?.length)
    sections.push({ title: 'Reply must contain (all)', tone: 'green', items: expect.responseMustContain });
  if (expect.responseMustContainAnyOf?.length)
    sections.push({ title: 'Reply must contain (any of)', tone: 'green', items: expect.responseMustContainAnyOf });
  if (expect.responseMustNotContain?.length)
    sections.push({ title: 'Reply must NOT contain', tone: 'red', items: expect.responseMustNotContain });

  if (sections.length === 0 && !expect.severityScreenComplete) {
    return (
      <div
        style={{
          padding: '10px 16px',
          fontSize: 11.5,
          color: isDark ? colors.slate[500] : colors.slate[400],
          fontStyle: 'italic',
        }}
      >
        Expectation block present but empty.
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {expect.severityScreenComplete && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12.5,
            color: isDark ? colors.amber[100] : colors.amber[700],
            background: isDark ? `${colors.amber[700]}33` : colors.amber[100],
            border: `1px solid ${colors.amber[600]}`,
            padding: '8px 10px',
            borderRadius: 8,
          }}
        >
          <SafetyOutlined />
          Severity screen must be complete (asks ≥ 3 of: fever / chest pain / blood / fluids /
          fainting).
        </div>
      )}
      {sections.map((sec, i) => (
        <ExpectationSection key={i} title={sec.title} tone={sec.tone} items={sec.items} isDark={isDark} />
      ))}
    </div>
  );
}

function ExpectationSection({
  title,
  tone,
  items,
  isDark,
}: {
  title: string;
  tone: 'green' | 'amber' | 'red' | 'violet';
  items: string[];
  isDark: boolean;
}) {
  const fg =
    tone === 'green' ? colors.green[600]
      : tone === 'amber' ? colors.amber[600]
      : tone === 'red' ? colors.red[600]
      : colors.violet[600];
  const bg =
    tone === 'green' ? (isDark ? `${colors.green[700]}22` : colors.green[100])
      : tone === 'amber' ? (isDark ? `${colors.amber[700]}22` : colors.amber[100])
      : tone === 'red' ? (isDark ? `${colors.red[700]}22` : colors.red[100])
      : (isDark ? `${colors.violet[700]}22` : colors.violet[50]);

  const Icon = tone === 'red' ? CloseCircleOutlined : CheckCircleOutlined;

  return (
    <div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: fg,
          marginBottom: 6,
        }}
      >
        <Icon /> {title}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {items.map((it) => (
          <span
            key={it}
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11.5,
              padding: '3px 9px',
              borderRadius: 6,
              background: bg,
              color: fg,
              border: `1px solid ${fg}33`,
            }}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Conversation rules ─────────────────────────────────────────────────────

function ConversationRules({
  rules,
  isDark,
}: {
  rules: ScenarioConversationAssertion[];
  isDark: boolean;
}) {
  if (rules.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No conversation-level rules. Per-turn expectations only."
      />
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rules.map((r, i) => (
        <div
          key={i}
          style={{
            background: isDark ? colors.slate[800] : '#FFFFFF',
            border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            borderRadius: 10,
            padding: '14px 16px',
          }}
        >
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: isDark ? colors.slate[50] : colors.slate[900],
              marginBottom: 8,
              letterSpacing: '-0.005em',
            }}
          >
            {r.rule ?? '(unnamed rule)'}
          </div>
          {r.mustNotAppearInAnyTurn?.length ? (
            <ExpectationSection
              title="Must NOT appear in any turn"
              tone="red"
              items={r.mustNotAppearInAnyTurn}
              isDark={isDark}
            />
          ) : null}
          {r.mustAppearAtLeastOnce?.length ? (
            <div style={{ marginTop: 8 }}>
              <ExpectationSection
                title="Must appear at least once"
                tone="green"
                items={r.mustAppearAtLeastOnce}
                isDark={isDark}
              />
            </div>
          ) : null}
          {r.requireNumbersGroundedInToolOutput && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12.5,
                color: isDark ? colors.amber[100] : colors.amber[700],
                background: isDark ? `${colors.amber[700]}22` : colors.amber[100],
                border: `1px solid ${colors.amber[600]}`,
                padding: '8px 10px',
                borderRadius: 8,
              }}
            >
              Numbers in agent replies must trace back to tool output (tolerance ±
              {r.groundingTolerance ?? 0.05}).
              {r.groundingIgnorePatterns && r.groundingIgnorePatterns.length > 0 && (
                <div
                  style={{
                    fontSize: 11.5,
                    fontFamily: "'JetBrains Mono', monospace",
                    marginTop: 4,
                  }}
                >
                  Ignored: {r.groundingIgnorePatterns.join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Run history ────────────────────────────────────────────────────────────

// ── Pending-run banner ─────────────────────────────────────────────────────
//
// Shown above the run-history list while a click-initiated run is in flight.
// Designed for a non-technical reader: clear "what's happening", a sense
// of how long it's been, and a friendly fallback message after 5 minutes.

function PendingRunBanner({ startedAt, isDark }: { startedAt: Date; isDark: boolean }) {
  // Tick once a second so the elapsed line stays current. mobx-react would
  // only re-render when the observable changed; we want a clock here.
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const elapsedSec = Math.floor((Date.now() - startedAt.getTime()) / 1000);
  const overFiveMin = elapsedSec > 300;

  let elapsed: string;
  if (elapsedSec < 5) elapsed = 'just now';
  else if (elapsedSec < 60) elapsed = `${elapsedSec} seconds ago`;
  else if (elapsedSec < 120) elapsed = '1 minute ago';
  else elapsed = `${Math.floor(elapsedSec / 60)} minutes ago`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        background: isDark ? `${colors.violet[700]}22` : colors.violet[50],
        border: `1px solid ${isDark ? colors.violet[700] : colors.violet[200]}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: isDark ? `${colors.violet[700]}33` : '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            border: `2px solid ${colors.violet[600]}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.9s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: isDark ? colors.violet[100] : colors.violet[700],
          }}
        >
          {overFiveMin ? 'Still running…' : 'Running this test now'}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: isDark ? colors.slate[300] : colors.slate[600],
            marginTop: 2,
          }}
        >
          {overFiveMin
            ? `Started ${elapsed}. This is taking longer than expected — leave the drawer open or come back to Recent runs in a few minutes.`
            : `Started ${elapsed}. The result will appear here automatically — no need to refresh.`}
        </div>
      </div>
    </div>
  );
}

function RunHistoryView({
  isLoading,
  rows,
  isDark,
  pendingStartedAt,
}: {
  isLoading: boolean;
  rows: ScenarioRunHistoryItem[];
  isDark: boolean;
  /** Set when a run kicked off from this drawer is still in flight. */
  pendingStartedAt: Date | null;
}) {
  // Local state for "which run's report is the user viewing right now."
  // Lifted into RunHistoryView (not the outer drawer) because the report
  // drawer is conceptually a child of the run history list — it never
  // opens from any other tab in the scenario drawer.
  const [reportRunId, setReportRunId] = useState<number | null>(null);
  const [reportPassed, setReportPassed] = useState<boolean | null>(null);
  const [reportSubtitle, setReportSubtitle] = useState<string>('');
  // While a run is pending, render a "Running now…" hero card at the
  // top — even if there's no other history yet. The empty state is
  // hidden in that case so the nurse never sees "no runs" the second
  // they kicked one off.
  const pendingBanner = pendingStartedAt ? (
    <PendingRunBanner startedAt={pendingStartedAt} isDark={isDark} />
  ) : null;

  if (isLoading && !pendingBanner) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (rows.length === 0 && !pendingBanner) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span style={{ fontSize: 13, color: isDark ? colors.slate[400] : colors.slate[500] }}>
            This test hasn't been run yet — click <b>Run</b> at the top of this drawer to try it.
          </span>
        }
      />
    );
  }
  if (rows.length === 0 && pendingBanner) {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{pendingBanner}</div>;
  }

  // Sparkline of pass/fail across the last N runs.
  const total = rows.length;
  const passes = rows.filter((r) => r.passed).length;
  const passRate = total > 0 ? Math.round((100 * passes) / total) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {pendingBanner}
      {/* Headline */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '12px 14px',
          background: isDark ? colors.slate[800] : '#FFFFFF',
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          borderRadius: 10,
        }}
      >
        <div>
          <Text style={{ fontSize: 11, color: isDark ? colors.slate[400] : colors.slate[500], textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block' }}>
            Pass rate (last {total})
          </Text>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: passRate >= 95 ? colors.green[600] : passRate >= 80 ? colors.amber[600] : colors.red[600],
              fontFamily: "'JetBrains Mono', monospace",
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {passRate}%
          </div>
        </div>
        {/* Pass/fail dot strip */}
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', flex: 1 }}>
          {rows.slice().reverse().map((r) => (
            <Tooltip key={r.resultId} title={`Run #${r.validationRunId} · ${r.passed ? 'pass' : 'fail'} · ${new Date(r.runDate).toLocaleString()}`}>
              <span
                style={{
                  width: 10,
                  height: 18,
                  borderRadius: 2,
                  background: r.passed ? colors.green[600] : colors.red[600],
                  display: 'inline-block',
                }}
              />
            </Tooltip>
          ))}
        </div>
      </div>

      {/* Per-run rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r) => (
          <div
            key={r.resultId}
            style={{
              display: 'grid',
              gridTemplateColumns: '24px 1fr auto auto auto auto',
              gap: 10,
              alignItems: 'center',
              padding: '8px 12px',
              background: isDark ? colors.slate[800] : '#FFFFFF',
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              borderRadius: 8,
              fontSize: 12.5,
            }}
          >
            {r.passed ? (
              <CheckCircleOutlined style={{ color: colors.green[600], fontSize: 16 }} />
            ) : (
              <CloseCircleOutlined style={{ color: colors.red[600], fontSize: 16 }} />
            )}
            <Text style={{ fontSize: 12.5, color: isDark ? colors.slate[200] : colors.slate[700] }}>
              {new Date(r.runDate).toLocaleString()}
            </Text>
            <Space size={4} wrap>
              {r.environment && <StatusPill tone="slate">{r.environment}</StatusPill>}
              {r.triggerSource && <StatusPill tone="ink">{r.triggerSource}</StatusPill>}
            </Space>
            <Text
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                color: isDark ? colors.slate[300] : colors.slate[600],
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {Number(r.score).toFixed(0)}%
            </Text>
            {r.failedAssertionCount > 0 ? (
              <StatusPill tone="red">{r.failedAssertionCount} fail</StatusPill>
            ) : (
              <StatusPill tone="green">all pass</StatusPill>
            )}
            {/* Per-row "View report" — opens the formatted HTML report
                in a 90%-width side drawer so a clinical SME can read
                the chat-bubble transcript without leaving the page. */}
            <Tooltip title="View the formatted report (chat-bubble transcript) for this run">
              <Button
                size="small"
                type="text"
                icon={<FileTextOutlined />}
                onClick={() => {
                  setReportRunId(r.validationRunId);
                  setReportPassed(r.passed);
                  setReportSubtitle(
                    `${new Date(r.runDate).toLocaleString()}` +
                    (r.environment ? ` · ${r.environment}` : '') +
                    (r.triggerSource ? ` · ${r.triggerSource}` : ''),
                  );
                }}
              >
                Report
              </Button>
            </Tooltip>
          </div>
        ))}
      </div>

      <HtmlReportDrawer
        runId={reportRunId}
        passed={reportPassed}
        subtitle={reportSubtitle}
        onClose={() => setReportRunId(null)}
      />
    </div>
  );
}

// ── Raw JSON view ──────────────────────────────────────────────────────────

function RawJsonView({ json, isDark }: { json: string; isDark: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => {
            void navigator.clipboard?.writeText(json);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? 'Copied' : 'Copy JSON'}
        </Button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: '14px 16px',
          background: isDark ? colors.slate[900] : '#FFFFFF',
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          borderRadius: 8,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          color: isDark ? colors.slate[200] : colors.slate[700],
          maxHeight: '60vh',
          overflow: 'auto',
        }}
      >
        {json}
      </pre>
    </div>
  );
}
